import {
  ESdChangeReason,
  EWFEventType,
  EWFNodeType,
  EWFStepType,
  IWFEvent,
  IWFLane,
  IWFNodeAssignment
} from "@kuika/kuika-cl-model";
import {
  EWFAssignmentType,
  EWFGatewayType,
  IMailInteraction,
  ITemplateParameter,
  IUserInteraction,
  IWFGateway,
  IWFNode,
  IWFStep
} from "@kuika/kuika-cl-model/src/models/workflow";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IPureDesignComponentProps, PureSdComponent } from "../../../../shared/pure-sd";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { WorkFlowBaseProperties } from "./workflow-base-properties";
import { WorkFlowBoundaryEventProperties } from "./workflow-event-properties/workflow-boundary-event-properties";
import { WorkFlowEventProperties } from "./workflow-event-properties/workflow-event-properties";
import { WorkFlowIntermediateCatchEventProperties } from "./workflow-event-properties/workflow-intermediate-catch-event-properties";
import { WorkFlowForwardProperties } from "./workflow-forward-properties";
import { WorkFlowGatewayProperties } from "./workflow-gateway-properties/workflow-gateway-properties";
import { WorkFlowLaneProperties } from "./workflow-lane-properties";
import { WorkFlowPhaseProperties } from "./workflow-phase-properties";
import { WorkflowLinkedProcessProperties } from "./workflow-step-properties/workflow-linkedprocess-properties";
import { WorkflowScreenStepProperties } from "./workflow-step-properties/workflow-screen-step-properties";
import { WorkflowSubprocessProperties } from "./workflow-step-properties/workflow-subprocess-properties";
import { WorkflowSystemStepProperties } from "./workflow-step-properties/workflow-system-step-properties";

export interface IWorkflowPropertiesState {
  stepType: EWFStepType | undefined;
  stepScreenId: string;
}

export interface IWorkflowPropertiesProps {}

class WorkflowProperties extends PureSdComponent<
  IWorkflowPropertiesProps & IWithSDProps & IWithContextProp,
  IWorkflowPropertiesState
> {
  constructor(props: IWorkflowPropertiesProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      stepType: undefined,
      stepScreenId: ""
    };
  }

  componentDidMount(): void {
    this.updateStepState();
  }

  componentDidUpdate(prevProps: IWorkflowPropertiesProps & IWithSDProps & IWithContextProp): void {
    if (
      prevProps.sd.selectedWfNode?.id !== this.props.sd.selectedWfNode?.id ||
      prevProps.sd.selectedWfNode?.nodeType !== this.props.sd.selectedWfNode?.nodeType ||
      prevProps.sd.selectedWf !== this.props.sd.selectedWf
    ) {
      this.updateStepState();
    }
  }

  updateStepState = () => {
    const step = this.props.sd.getSelectedWfStep();
    this.setState({
      stepType: step?.stepType,
      stepScreenId: step?.id || ""
    });
  };

  laneAssignmentChangeHandler = async (lane: IWFLane, assignment: IWFNodeAssignment) => {
    this.props.sd.handleLaneAssignmentChange(lane, assignment);
  };

  stepAssignmentChangeHandler = async (
    useLaneAssignment: boolean,
    assignment: IWFNodeAssignment,
    notify?: boolean,
    addUserInteractionsToMail?: boolean,
    addGoToTaskButtonToMail?: boolean,
    mailInteractions?: IMailInteraction[],
    userInteractions?: IUserInteraction[],
    useCustomTemplate?: boolean,
    templateId?: string,
    templateParameters?: ITemplateParameter[]
  ) => {
    const wf = this.props.sd.selectedWf;
    const currentStep = this.props.sd.findStepRecursively(wf!, (s) => s.id === this.props.sd.selectedWfNode?.id!);
    if (wf && wf.nodes && currentStep) {
      if (useLaneAssignment) {
        const stepElement = document.querySelector(`[data-element-id=${this.props.sd.selectedWfNode?.id}]`);
        for (const lane of wf.nodes.lanes) {
          const laneElement = document.querySelector(`[data-element-id=${lane.id}]`);
          const isOverlap = this.props.sd.elementsOverlap(laneElement, stepElement);
          if (isOverlap) {
            if (currentStep) {
              currentStep.assignment = lane.assignment;
            }
            break;
          }
        }
      } else if (currentStep) {
        currentStep.assignment = assignment;
      }
      currentStep.useLaneAssignment = useLaneAssignment;

      if (currentStep.assignment.assignmentType === EWFAssignmentType.external) {
        currentStep.notifyUser = false;
        currentStep.addUserInteractionsToMail = false;
        currentStep.addGoToTaskButtonToMail = false;
        currentStep.mailInteractions = [];
        currentStep.selectedAppIdsForNotification = [];
      }

      if (notify && notify !== undefined) {
        currentStep.notifyUser = notify;
      }

      if (addUserInteractionsToMail) {
        currentStep.addUserInteractionsToMail = addUserInteractionsToMail;
      }

      if (addGoToTaskButtonToMail) {
        currentStep.addGoToTaskButtonToMail = addGoToTaskButtonToMail;
      }

      if (mailInteractions) {
        currentStep.mailInteractions = mailInteractions;
      }

      if (userInteractions) {
        currentStep.userInteractions = userInteractions;
      }

      if (useCustomTemplate) {
        currentStep.useCustomTemplate = useCustomTemplate;
      }

      if (templateId) {
        currentStep.templateId = templateId;
      }

      if (templateParameters) {
        currentStep.templateParameters = templateParameters;
      }

      await this.props.sd.saveWorkFlow(wf, ESdChangeReason.WFStepMappingChange);
    }
  };

  // Node-type labels are shown UPPERCASE in the panel header ("START EVENT", "USER TASK").
  // The literals are written uppercase (not `.toUpperCase()`) so the output is byte-stable
  // regardless of the current culture (the Turkish-i trap).
  printNodeType = (node: IWFNode | undefined): string => {
    if (node) {
      switch (node.nodeType) {
        case EWFNodeType.Event:
          const event = node as IWFEvent;
          if (event.eventType === EWFEventType.Start) {
            return "START EVENT";
          }
          if (event.eventType === EWFEventType.End) {
            return "END EVENT";
          }
          if (event.eventType === EWFEventType.Boundary) {
            return "BOUNDARY EVENT";
          }
          if (event.eventType === EWFEventType.Intermediate) {
            return "INTERMEDIATE EVENT";
          }
          return "EVENT";
        case EWFNodeType.Step:
          const step = node as IWFStep;
          if (step.stepType === EWFStepType.ScreenInteraction) {
            return "USER TASK";
          }
          if (step.stepType === EWFStepType.Subprocess) {
            return "SUBPROCESS";
          }
          if (step.stepType === EWFStepType.LinkedProcess) {
            return "LINKED PROCESS";
          }
          return "SYSTEM STEP";
        case EWFNodeType.Gateway:
          const gateway = node as IWFGateway;
          if (gateway.gateWayType === EWFGatewayType.Conditional) return "EXCLUSIVE GATEWAY";
          if (gateway.gateWayType === EWFGatewayType.Paralel) return "PARALLEL GATEWAY";
          if (gateway.gateWayType === EWFGatewayType.Inclusive) return "INCLUSIVE GATEWAY";
          return "GATEWAY";
        case EWFNodeType.Lane:
          const lane = node as IWFLane;
          return "LANE";
        case EWFNodeType.Arrow:
          return "PATH";
        default:
          return "";
      }
    }
    return "";
  };

  render() {
    const wf = this.props.sd.selectedWf;

    let selectedEvent: IWFEvent | undefined;
    if (this.props.sd.selectedWfNode?.id && wf) {
      selectedEvent = this.props.sd.findNodeRecursively(this.props.sd.selectedWfNode.id, EWFNodeType.Event, wf) as
        | IWFEvent
        | undefined;

      if (!selectedEvent) {
        const stepWithBoundaryEvent = this.props.sd.findStepRecursively(
          wf,
          (step) => step.boundaryEvent?.id === this.props.sd.selectedWfNode?.id
        );
        selectedEvent = stepWithBoundaryEvent?.boundaryEvent;
      }
    }

    if (!this.props.sd.selectedWf || this.props.sd.selectedWf.id.length === 0) {
      return null;
    }

    const selectedNode: IWFNode | undefined = this.props.sd.selectedWfNode;
    const isStepNode: boolean = selectedNode?.nodeType === EWFNodeType.Step;
    const nodeTypeLabel: string = this.printNodeType(selectedNode);
    // The step header reads as the step's own name (matching the design's "LoremIpsum"),
    // falling back to the type label before it is named. Other node types lead with the
    // uppercase type and keep the name as a subtitle only when it adds information (not the
    // process name echoed back onto its start/end event).
    const nodeHeaderTitle: string = isStepNode && selectedNode?.name ? selectedNode.name : nodeTypeLabel;
    // The subtitle is the node's own name, shown only when it adds information: not for a
    // step (its name is already the title), not when it just echoes the process name, and
    // not when it is redundant with the type (a Start event auto-named "START" under a
    // "START EVENT" title). `toUpperCase` is locale-independent in JS, so the redundancy
    // check is culture-safe. Kept for genuinely-named nodes (a lane's role, a gateway label).
    const nodeName: string | undefined = selectedNode?.name || undefined;
    const subtitleRedundant: boolean =
      !nodeName || nodeName === wf?.name || nodeTypeLabel.includes(nodeName.toUpperCase());
    const nodeHeaderSubtitle: string | undefined = !isStepNode && !subtitleRedundant ? nodeName : undefined;
    const showNodeHeader: boolean = !!selectedNode?.nodeType;

    return (
      <>
        {!this.props.sd.selectedWfNode?.id && !this.props.sd.selectedWfPhaseId && (
          <div>
            <WorkFlowBaseProperties wf={wf}></WorkFlowBaseProperties>
          </div>
        )}
        {this.props.sd.selectedWfPhaseId && (
          <>
            <div className="kuika_workflow-properties__step-name-wrapper">
              <span className="kuika_workflow-properties__node-text">
                <span className="kuika_workflow-properties__node-title">PHASE</span>
              </span>
            </div>
            <WorkFlowPhaseProperties
              phaseId={this.props.sd.selectedWfPhaseId}
              key={this.props.sd.selectedWfPhaseId}
            ></WorkFlowPhaseProperties>
          </>
        )}
        {showNodeHeader && (
          <div className="kuika_workflow-properties__step-name-wrapper">
            {isStepNode && (
              <KuikaSvg
                name="components"
                width={16}
                height={16}
                color="var(--dark-9)"
                className="kuika_workflow-properties__node-icon"
              />
            )}
            {/* The header IS how the panel says which node it is describing, so it carries the
                hooks: a test that selects a node has nothing else to assert on. */}
            <span className="kuika_workflow-properties__node-text">
              <span className="kuika_workflow-properties__node-title" data-testid="wfNodeHeaderTitle">
                {nodeHeaderTitle}
              </span>
              {nodeHeaderSubtitle && (
                <span className="kuika_workflow-properties__node-subtitle" data-testid="wfNodeHeaderSubtitle">
                  {nodeHeaderSubtitle}
                </span>
              )}
            </span>
          </div>
        )}
        <div className="row kuika_topPanel btn-group-vertical kuika_wfParameters">
          {this.props.sd.selectedWfNode?.nodeType === EWFNodeType.Step &&
            this.props.sd.getSelectedWfStep()?.stepType == EWFStepType.ScreenInteraction && (
              <WorkflowScreenStepProperties
                onAssignmentChange={(
                  toggleLane,
                  assignment,
                  notify,
                  addUserInteractionsToMail,
                  addGoToTaskButtonToMail,
                  mailInteractions,
                  userInteractions,
                  useCustomTemplate,
                  templateId,
                  templateParameters
                ) =>
                  this.stepAssignmentChangeHandler(
                    toggleLane,
                    assignment,
                    notify,
                    addUserInteractionsToMail,
                    addGoToTaskButtonToMail,
                    mailInteractions,
                    userInteractions,
                    useCustomTemplate,
                    templateId,
                    templateParameters
                  )
                }
                key={`${this.props.sd.selectedWfNode.id}`}
              ></WorkflowScreenStepProperties>
            )}
          {this.props.sd.selectedWfNode?.nodeType === EWFNodeType.Step &&
            this.props.sd.getSelectedWfStep()?.stepType == EWFStepType.BackendInteraction && (
              <WorkflowSystemStepProperties key={this.props.sd.selectedWfNode.id}></WorkflowSystemStepProperties>
            )}
          {this.props.sd.selectedWfNode?.nodeType === EWFNodeType.Step &&
            this.props.sd.getSelectedWfStep()?.stepType == EWFStepType.Subprocess && (
              <WorkflowSubprocessProperties
                selectedNodeId={this.props.sd.selectedWfNode.id}
              ></WorkflowSubprocessProperties>
            )}
          {this.props.sd.selectedWfNode?.nodeType === EWFNodeType.Step &&
            this.props.sd.getSelectedWfStep()?.stepType == EWFStepType.LinkedProcess && (
              <WorkflowLinkedProcessProperties
                selectedNodeId={this.props.sd.selectedWfNode.id}
              ></WorkflowLinkedProcessProperties>
            )}
          {this.props.sd.selectedWfNode?.nodeType === EWFNodeType.Event &&
            selectedEvent !== undefined &&
            selectedEvent.eventType == EWFEventType.Boundary && (
              <WorkFlowBoundaryEventProperties
                selectedNodeId={this.props.sd.selectedWfNode.id}
                key={this.props.sd.selectedWfNode.id}
                eventTag={selectedEvent.eventTag}
              ></WorkFlowBoundaryEventProperties>
            )}
          {this.props.sd.selectedWfNode?.nodeType === EWFNodeType.Event &&
            selectedEvent !== undefined &&
            selectedEvent.eventType == EWFEventType.Intermediate && (
              <WorkFlowIntermediateCatchEventProperties
                selectedNodeId={this.props.sd.selectedWfNode.id}
                key={this.props.sd.selectedWfNode.id}
              ></WorkFlowIntermediateCatchEventProperties>
            )}
          {this.props.sd.selectedWfNode?.nodeType === EWFNodeType.Event &&
            selectedEvent !== undefined &&
            (selectedEvent.eventType == EWFEventType.Start || selectedEvent.eventType == EWFEventType.End) && (
              <WorkFlowEventProperties
                selectedNodeId={this.props.sd.selectedWfNode.id}
                key={this.props.sd.selectedWfNode.id}
              ></WorkFlowEventProperties>
            )}
          {this.props.sd.selectedWfNode?.nodeType === EWFNodeType.Arrow && (
            <WorkFlowForwardProperties decisionId={this.props.sd.selectedWfNode?.id ?? ""}></WorkFlowForwardProperties>
          )}
          {this.props.sd.selectedWfNode?.nodeType === EWFNodeType.Gateway && (
            <WorkFlowGatewayProperties
              key={this.props.sd.selectedWfNode.id}
              // onExclusiveConditionOrderChange={(decisions) => this.decisionUpdateHandler(decisions)}
            ></WorkFlowGatewayProperties>
          )}
          {this.props.sd.selectedWfNode?.nodeType === EWFNodeType.Lane && (
            <WorkFlowLaneProperties
              selectedNodeId={this.props.sd.selectedWfNode.id}
              assignmenChangeHandler={(lane, assignment) => this.laneAssignmentChangeHandler(lane, assignment)}
            ></WorkFlowLaneProperties>
          )}
        </div>
      </>
    );
  }

  checkNeedRender = (
    changeReason: ESdChangeReason,
    nextProps: IPureDesignComponentProps & IWithSDProps & IWorkflowPropertiesProps
  ): boolean => {
    if (
      changeReason === ESdChangeReason.ClickWfDesignXmlNode ||
      changeReason === ESdChangeReason.SaveWorkflow ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const workflowProperties = withContext<IWorkflowPropertiesProps>(withSD<IWorkflowPropertiesProps>(WorkflowProperties));
export { workflowProperties as WorkflowProperties };
